import { ReferentialKey } from "api/referential"
import { useAppSelector } from "app/hooks"
import { Button } from "components/ui/Button"
import { GenericCardInfos } from "components/display/Card/CardInfos"
import { CommercialIcon, ManIcon, WomanIcon } from "components/icons/customIcons"
import { PenIcon, TrashIcon } from "components/icons/Icons"
import { ActorType } from "features/new-actor/enums/actorType"
import { LegalEntityContactFormValues } from "features/new-actor/types/legalEntityContactFormValues"
import { NaturalPersonFormValues } from "features/new-actor/types/optim/naturalPersonFormValues"
import { selectReferentialByKey } from "features/referentials/selectors/selectReferentialByKey"
import { FC, useCallback, useMemo } from "react"

interface UpiContactCardProps {
  entity: LegalEntityContactFormValues
  onUpdate: (formValues: NaturalPersonFormValues) => void
  onDelete?: (id: string) => void
  /** Contact invalide au sens de la validation du formulaire PM (champs manquants). */
  hasError?: boolean
  /** Reason of the error when the validation gives one on the contact itself (e.g. duplicated email). */
  errorMessage?: string
  /** Narrow container (side panel): tighter padding. */
  compact?: boolean
}

export const UpiContactCard: FC<UpiContactCardProps> = ({ entity, onUpdate, onDelete, hasError, errorMessage, compact = false }) => {
  const mapIcon = useCallback((key: string) => {
    switch (key) {
      case "Mr":
        return <ManIcon />
      case "Mme":
        return <WomanIcon />
      case ActorType.Legal:
        return <CommercialIcon className="text-primary w-6" />
    }
  }, [])

  const companyRoleReferential = useAppSelector(selectReferentialByKey(ReferentialKey.CompanyRole))

  const companyRole = useMemo(() => {
    return companyRoleReferential?.find((item) => item.key === entity.companyRole)?.label;
  }, [entity, companyRoleReferential])

  const renderBody = useMemo(() => {
    return <>
      {/* Room kept on the right for the two round buttons of the card. */}
      <div className="flex gap-2 items-center min-w-0 pr-24">
        <span className="flex-none">{mapIcon(entity.civility)}</span>
        <p className="font-bold truncate">
          {`${entity.civility} ${entity.lastName.toUpperCase()} ${entity.firstName}`}
        </p>
      </div>
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm truncate" title={entity.email}>{entity.email}</p>
          <p className="text-sm">{entity.phone}</p>
        </div>
        <div className="flex flex-none items-end justify-end">
          {
            companyRole?
            <p className="rounded-full px-2 py-1 text-sm bg-primary/10 text-primary inline-block">{companyRole}</p>
            :
            <p className="rounded-full px-2 py-1 text-sm bg-red-50 text-red-400 inline-block">Role manquant</p>
          }
        </div>
      </div>
      {hasError && (
        <p className="text-sm text-red-500">
          {errorMessage ?? "Informations incomplètes — ouvrez la fiche pour les compléter"}
        </p>
      )}
    </>
  }, [entity, mapIcon, companyRole, hasError, errorMessage]);

  return <GenericCardInfos
    className={`grow ${compact ? '!p-5' : ''} ${hasError ? 'border-red-400' : ''}`}
    aside={
      <div className="flex gap-2 self-baseline">
        <Button color="slate" round onClick={() => onUpdate(entity)}>
          <PenIcon className="text-black" />
        </Button>
        {onDelete && entity.id && (
          <Button color="slate" round onClick={() => onDelete(entity.id!)}>
            <TrashIcon className="w-5 h-5 text-black" />
          </Button>
        )}
      </div>
    }
  >
    {renderBody}
  </GenericCardInfos>
}
